import { useCallback, useMemo } from 'react'
import type { ReactNode } from 'react'
import { NoCourse } from '~/components/course/NoCourse'
import { DateTimeSelection } from '~/features/calendar/DateTimeSelection'
import { InitAtom } from '~/features/calendar/InitAtom'
import { CourseSection } from '~/features/guide/courses/CourseSection'
import { scope as courseScope } from '~/features/guide/courses/courseList'
import type { RestaurantId } from '~/lib/graphql/scalars'
import type { MealPeriodTab } from '~/lib/mealPeriod/tab'
import type { AdditionalCriteria, GuideContext, GuideQuery } from '~/navigation'
import {
  useReserveNavigate,
  useReserveNavigationState,
  useReserveOverlayContext,
} from '~/navigation'
import type { VisitCondition } from '~/stores/calendar/state'
import {
  scope as calendarScope,
  useClearCondition,
  useHasReservableInventory,
} from '~/stores/calendar/state'
import type { InitEvent } from '~/stores/calendar/statemachine'
import { useToday } from '~/stores/now'
import { css, cx, sva } from '~/styled-system/css'
import { center, vstack } from '~/styled-system/patterns'
import InventoryNotificationComponent from '../InventoryNotification'

const styles = sva({
  slots: ['container', 'main', 'sticky'],
  base: {
    container: {
      width: 'content.base',
      mx: 'auto',
      display: 'flex',
      gap: '5',
      alignItems: 'stretch',
      minHeight: '[1200px]',
    },
    main: vstack.raw({
      // flex: '1' だと CourseSection の絞り込み FilterChips が必要なだけ伸びてしまう..
      width: '[620px]',
      boxSizing: 'border-box',
      gap: '[80px]',
      alignItems: 'stretch',
    }),
    sticky: {
      position: 'sticky',
      alignSelf: 'flex-start',
      boxSizing: 'border-box',
      width: '[360px]',
      top: '10',
      border: 'medium',
      borderRadius: 'default',
      // カレンダー上の余白40pxと下も詰まっていると見づらいので+10pxの余白を持たせる
      maxHeight: '[calc(100vh - 50px)]',
      // 一休の日ポイントアップの吹き出しがはみ出すのを許容するため
      overflowY: 'visible',
    },
  },
})

type Props = {
  header: ReactNode
  footer: ReactNode
  showInventoryNotification: boolean
  showIkyuDayBalloon: boolean
  phoneNumber?: string
}

// TODO [見た目調整] CourseSection は padding ではなく margin で外部から与える
/**
 * ガイドのコース一覧とカレンダーのコンテナコンポーネント
 *
 * 絞り込み条件を連動させている。
 */
export function CourseCalendar(props: Props) {
  return (
    <calendarScope.Provider>
      <courseScope.Provider>
        <Body {...props} />
      </courseScope.Provider>
    </calendarScope.Provider>
  )
}

function Body({
  header,
  footer,
  showInventoryNotification,
  showIkyuDayBalloon,
  phoneNumber,
}: Props) {
  // 直ランディング可能なガイドの上にモーダル・ふすまが出る状態のとき表示必要
  const context = useReserveOverlayContext(
    'GUIDE',
    'PHOTO',
    'REVIEWS',
    'ACCESS',
    'DETAILS',
    'HIGHLIGHTS',
    'PLAN_MODAL',
    'COURSE_SELECT',
    'CALENDAR',
    'NEW_RESERVE_INPUT'
  )
  const today = useToday()

  const {
    restaurantId,
    visitDate,
    visitTime,
    visitorsCount,
    mealPeriod,
    childUses,
    seatTypes,
    smokingAllowed,
    windowSide,
    buffet,
    freeFlow,
    sceneTagIds,
    upperBoundPrice,
    lowerBoundPrice,
  } = (context as GuideContext) ?? {} // プラン直ランディングのときは mealPeriod 以下が存在しないが undefined でよいので強制キャストで逃げている

  // 条件の連動は navigation event による guide to guide への遷移で context の変化で行われる
  // CourseSection での絞り込みは内部で updateFilterCriteria イベントを発火させる
  // DateTimeSelection での人数日時変更は SyncCalendarToCourses 経由で changeReserveCondition イベントを発火させる
  const query = {
    visitDate,
    visitTime,
    visitorsCount,
    mealPeriod,
    childUses,
    seatTypes,
    smokingAllowed,
    windowSide,
    buffet,
    freeFlow,
    sceneTagIds,
  } as const satisfies GuideQuery

  // ガイドが再前面 (screen stack の top) 以外ではカレンダーイベントを無視する
  // 予約入力などのモーダルやふすまが開いているときにイベントで navigation が起きると、
  // ランディング時に不要なアニメーションのトリガーになってしまう
  const isBackground = useReserveNavigationState('GUIDE') === undefined
  const navigate = useReserveNavigate()

  const onVisitConditionChange = useCallback(
    (condition: VisitCondition) => {
      if (isBackground) {
        return
      }
      navigate({
        type: 'changeCondition',
        ...condition,
      })
    },
    [isBackground, navigate]
  )

  const onMealPeriodChange = useCallback(
    (mealPeriod: MealPeriodTab) => {
      if (isBackground) {
        return
      }
      navigate({
        type: 'changeMealPeriod',
        mealPeriod,
      })
    },
    [isBackground, navigate]
  )

  const onClear = useCallback(() => {
    navigate({ type: 'clearCondition' })
  }, [navigate])

  const criteria: AdditionalCriteria = useMemo(
    () => ({
      childUses,
      seatTypes,
      smokingAllowed,
      windowSide,
      buffet,
      freeFlow,
      sceneTagIds,
      upperBoundPrice,
      lowerBoundPrice,
    }),
    [
      buffet,
      childUses,
      freeFlow,
      lowerBoundPrice,
      sceneTagIds,
      seatTypes,
      smokingAllowed,
      upperBoundPrice,
      windowSide,
    ]
  )

  const initEvent: InitEvent = useMemo(
    () => ({
      calendarClosed: false,
      today,
      restaurantId: restaurantId!,
      phoneNumber,
      selectedVisitorsCount: visitorsCount,
      selectedVisitDate: visitDate,
      selectedVisitTime: visitTime,
      selectedMealPeriod: mealPeriod,
      criteria,
    }),
    [criteria, mealPeriod, phoneNumber, restaurantId, today, visitDate, visitTime, visitorsCount]
  )

  const classes = styles()
  return (
    <div className={classes.container}>
      <div className={classes.main}>
        {header}
        <CourseSection
          restaurantId={restaurantId!}
          query={query}
          hideClear={true}
          defaultOpen={true}
        />
        {footer}
      </div>
      <InitAtom
        initEvent={initEvent}
        mealPeriod={mealPeriod}
        criteria={criteria}
        onVisitConditionChange={onVisitConditionChange}
        onMealPeriodChange={onMealPeriodChange}
        onClearCondition={onClear}
      />
      <CalendarContainer
        restaurantId={restaurantId!}
        className={classes.sticky}
        showInventoryNotification={showInventoryNotification}
        showIkyuDayBalloon={showIkyuDayBalloon}
      />
    </div>
  )
}

function CalendarContainer({
  restaurantId,
  className,
  showInventoryNotification,
  showIkyuDayBalloon,
}: {
  restaurantId: RestaurantId
  className?: string
  showInventoryNotification: boolean
  showIkyuDayBalloon: boolean
}) {
  // PC ガイドには二週間カレンダーがないので、明示的に予約可能かどうかを判断しないといけない
  const reservable = useHasReservableInventory()

  // reservable が undefined の場合は Calendar コンポーネントを表示しローディングをだすようにする
  if (reservable === false) {
    return (
      <div className={cx(className, vstack({ p: '4', gap: '5', alignItems: 'center' }))}>
        <NoCourse
          restaurantId={restaurantId!}
          withClearButton={false}
          mainText="現在、予約可能日はございません。"
        />
        {showInventoryNotification && (
          <InventoryNotificationComponent
            withSpread
            restaurantId={restaurantId}
            buttonClassName="w-full"
          />
        )}
      </div>
    )
  }

  return <Calendar className={className} showIkyuDayBalloon={showIkyuDayBalloon} />
}

function Calendar({
  className,
  showIkyuDayBalloon,
}: { className?: string; showIkyuDayBalloon: boolean }) {
  return (
    <div className={className}>
      <DateTimeSelection
        mode="pc"
        showIkyuDayBalloon={showIkyuDayBalloon}
        clear={<ClearButton />}
      />
    </div>
  )
}

function ClearButton() {
  const clear = useClearCondition()
  return (
    <div className={center()}>
      <button type="button" onClick={clear} className={css({ color: 'link' })}>
        人数・日時・絞り込み条件をクリア
      </button>
    </div>
  )
}
